import {
  IonContent,
  IonPage,
  useIonViewDidEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import React, { useState } from 'react';
import useEnhancedAuth from '../EnhancedAuth';
import './Default.css';
import { apiSavePassword } from '../services/api';
import AppPageHeader from '../components/AppPageHeader';
import { returnToMain } from '../utils/nav';
import { useTranslation } from 'react-i18next';
import {
  strHasLower,
  strHasMinLength,
  strHasNumber,
  strHasSpecial,
  strHasUpper,
} from '../utils/strings';
import {
  getAppError,
  getAppSuccess,
  getUserMessageOrValidationError,
} from '../utils/messages';
import { cfgPasswordMinLength } from '../utils/config';
import AccountPasswordForm from '../usecases/account/AccountPasswordForm';
import { useHistory } from 'react-router';
import { LoadingController } from '../utils/LoadingController';
import { useToaster } from '../hooks/useToaster';

const AccountPasswordPage: React.FC = () => {
  const auth = useEnhancedAuth();
  const [t] = useTranslation('global');
  const router = useHistory();
  const [present] = useToaster();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [old, setOld] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [password2, setPassword2] = useState<string>('');

  const [isPageActive, setIsPageActive] = useState(false);
  useIonViewDidEnter(() => {
    setIsPageActive(true);
  });
  useIonViewWillLeave(() => {
    setIsPageActive(false);
  });

  const getSecurityValidationError = () => {
    if (!old) {
      return t('changepw.errorold');
    }
    if (!password) {
      return t('register.errorpassword');
    }
    if (password != password2) {
      return t('register.errorpassword2');
    }
    if (
      !strHasMinLength(password, cfgPasswordMinLength()) ||
      !strHasLower(password) ||
      !strHasUpper(password) ||
      !strHasNumber(password) ||
      !strHasSpecial(password)
    ) {
      return t('register.errorpasswordquality');
    }
    return false;
  };

  const saveChanges = async () => {
    if (isSubmitting) {
      return;
    }

    const validationError = getSecurityValidationError();
    if (validationError !== false) {
      present(getAppError(validationError));
      return;
    }

    try {
      LoadingController.show();
      setIsSubmitting(true);
      await apiSavePassword(
        {
          oldPassword: old,
          newPassword: password,
        },
        auth,
      );
      present(getAppSuccess(t('changepw.success')));
    } catch (error) {
      console.error('Error changing password ', error);
      const apiError = getUserMessageOrValidationError(error);
      if (typeof apiError === 'string') {
        present(getAppError(apiError));
      } else {
        present(getAppError(t('changepw.nosuccess')));
      }
    } finally {
      setIsSubmitting(false);
      LoadingController.hide();
    }
  };

  return (
    <IonPage id="accountpassword-page">
      <AppPageHeader
        isPageActive={isPageActive}
        back={() => returnToMain(router)}
        title={t('changepw.title')}
      ></AppPageHeader>
      <IonContent fullscreen className="ion-padding default-content">
        <AccountPasswordForm
          isValid={getSecurityValidationError() === false}
          old={old}
          proceed={saveChanges}
          setOld={setOld}
          password={password}
          password2={password2}
          setPassword={setPassword}
          setPassword2={setPassword2}
        ></AccountPasswordForm>
      </IonContent>
    </IonPage>
  );
};

export default AccountPasswordPage;
